// Formatting + client-side export helpers.

export function fmtNumber(value: any, digits = 2): string {
  const n = typeof value === "number" ? value : parseFloat(value);
  if (n === null || n === undefined || Number.isNaN(n) || !Number.isFinite(n)) return "N/A";
  const formatted = n.toLocaleString("en-IN", { maximumFractionDigits: digits, minimumFractionDigits: 0 });
  return formatted;
}

export function isNumericValue(value: any): boolean {
  if (typeof value === "number") return Number.isFinite(value);
  return false;
}

/** Remove status emoji from strings for elegant badge display. */
export function cleanStatusText(raw: string): string {
  return String(raw || "")
    .replace(/[\u{1F7E2}\u{1F534}\u{1F7E1}\u26AA\u2705\u274C\u2796\u{1F41D}\u{1F36F}]/gu, "")
    .trim();
}

export function toCsv(rows: Record<string, any>[], columns?: string[]): string {
  if (!rows || !rows.length) return "";
  const cols = columns || Object.keys(rows[0]);
  const escape = (v: any) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [cols.map(escape).join(",")];
  for (const row of rows) lines.push(cols.map((c) => escape(row[c])).join(","));
  return lines.join("\n");
}

/**
 * Apple requires every browser on iOS (Safari, Chrome/CriOS, Firefox/FxiOS, ...)
 * to run on the same underlying WebKit engine, which does not reliably honour
 * the `download` attribute on blob URLs - it silently no-ops instead of saving
 * the file, especially once the click handler has crossed an async/await
 * boundary. This is a platform (WebKit) limitation, not a Safari-specific one,
 * so it's detected by device rather than browser brand. Opening the blob in a
 * new tab instead lets the built-in viewer render it, from which the user can
 * use the Share Sheet to save it.
 */
function isIosDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

// True only for an iOS "Add to Home Screen" install, running in its own
// standalone WKWebView with no Safari chrome - `navigator.standalone` is the
// iOS-specific flag for this (display-mode: standalone covers the same case
// on other platforms, kept as a fallback for completeness, not because it's
// expected to fire here since isIosDevice() already gates the caller).
function isStandalonePwa(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia?.("(display-mode: standalone)")?.matches === true;
}

export async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const file = new File([blob], filename, { type: blob.type || "application/octet-stream" });
  const nav = navigator as Navigator & {
    canShare?: (data: { files?: File[] }) => boolean;
    share?: (data: { files?: File[] }) => Promise<void>;
  };
  const canShareFile = !!(nav.canShare && nav.share && nav.canShare({ files: [file] }));

  if (isIosDevice()) {
    // A regular Safari tab has a tab strip for window.open to open a new
    // browsing context into - it lands on WebKit's built-in PDF viewer, from
    // which the Share Sheet can save the file. An installed "Add to Home
    // Screen" app runs in standalone mode with NO tab strip, so that same
    // window.open call has nowhere to open a new tab into and silently
    // no-ops - no error is thrown, so the caller still reports success with
    // no PDF ever appearing (the bug this branch exists to avoid). Standalone
    // WKWebView apps do support the native Share Sheet for files, so try
    // that first; only fall back to navigating the current view to the blob
    // URL (WKWebView still renders a PDF blob in place) if sharing itself
    // isn't available or fails for a reason other than the user cancelling.
    if (isStandalonePwa()) {
      if (canShareFile) {
        try {
          await nav.share!({ files: [file] });
          return;
        } catch (err) {
          if ((err as any)?.name === "AbortError") return; // user dismissed the share sheet
        }
      }
      const url = URL.createObjectURL(blob);
      window.location.href = url;
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      return;
    }

    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    return;
  }

  // Android in-app browsers (the Google app's embedded tab, Instagram/
  // Facebook in-app browsers, etc.) run a stripped-down WebView that
  // silently ignores anchor[download] clicks on blob: URLs - the click
  // fires and throws nothing, so a naive caller reports success even
  // though no file was ever saved. The Web Share API hands the blob to
  // the host OS instead, which these WebViews do support, surfacing the
  // native share sheet so the user can pick "Save to Files" or any PDF
  // viewer. Full desktop/mobile Chrome supports this API too, so it's
  // tried first everywhere except regular (non-standalone) iOS Safari,
  // handled above, where window.open is the more reliable path.
  if (canShareFile) {
    try {
      await nav.share!({ files: [file] });
      return;
    } catch (err) {
      if ((err as any)?.name === "AbortError") return; // user dismissed the share sheet
      // Sharing failed for some other reason - fall through and try the
      // direct-download path below rather than leaving the user with nothing.
    }
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

export function downloadCsv(rows: Record<string, any>[], filename: string, columns?: string[]) {
  const csv = toCsv(rows, columns);
  downloadBlob(new Blob([csv], { type: "text/csv;charset=utf-8" }), filename);
}

export function downloadJson(obj: any, filename: string) {
  downloadBlob(new Blob([JSON.stringify(obj, null, 2)], { type: "application/json" }), filename);
}

export function downloadText(text: string, filename: string, mime = "text/plain") {
  downloadBlob(new Blob([text], { type: mime }), filename);
}

export function base64ToBlob(base64: string, mime = "application/pdf"): Blob {
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/** Simple per-action cooldown registry to prevent duplicate clicks. */
const cooldowns: Record<string, { time: number; signature: string }> = {};

export function checkCooldown(action: string, seconds: number, signature = ""): { ok: boolean; wait: number } {
  const now = Date.now();
  const prev = cooldowns[action];
  if (prev && prev.signature === signature && now - prev.time < seconds * 1000) {
    return { ok: false, wait: Math.max(1, Math.ceil((seconds * 1000 - (now - prev.time)) / 1000)) };
  }
  cooldowns[action] = { time: now, signature };
  return { ok: true, wait: 0 };
}
